import Peer from 'skyway-js';
import logger from '@/modules/Logger';
import UAParser from 'ua-parser-js';

function _open(obj) {
  return new Promise(resolve => {
    obj.on('open', peerId => resolve(peerId));
  });
}

/**
 * SkyWay's adapter for client
 */
class SkywayAdapter {
  constructor (Option) {
    this.APIKey = process.env.VUE_APP_SKYWAY_API_KEY;
    this.userId = Option.userId || null;
    this.remoteUsers = Option.remoteUsers;
    this.events = [];
    this.stream = null;
    this.meshRoom = null;
    this.videoDevice = Option.videoDevice;
    this.audioDevice = Option.audioDevice;
    this.isScreenShare = false;
    this.enableAudio = Option.enableAudio;
    this.enableVideo = Option.enableVideo;
    this.rtcServer = '';
    this.socket = Option.socket;
    this.peer = null;
    this.roomSet = null;
    this.virtualBackground = Option.virtualBgModel;
    this.videoEffect = Option.videoEffect;
    this.lessonId = Option.lessonId;

    // Skyway peer connection
    try {
      this.peer = new Peer(this.userId, {
        key: this.APIKey,
        credential: Option.credential,
        debug: 1 // Log level. NONE: 0, ERROR: 1, WARN: 2, FULL: 3.
      });
      logger.info('[Skyway] Success on create peer', { operation: 'webrtc:peer' });
    } catch (err) {
      logger.error('[Skyway] Error on create peer', { operation: 'webrtc:peer', payload: err.message || err,event_type:55,lesson_id:this.lessonId });
    }

    // constraints of the video stream
    this.videoProfile = {
      width: Option.videoProfile.width,
      height: Option.videoProfile.height,
    };
    this.constraints = {
      audio: true,
      video: this.videoProfile,
    };
    // constraints of the share screen stream
    this.shareScreenConstraints = {
      video: {
        width: Option.screenShareProfile.width,
        height: Option.screenShareProfile.height
      }
    };
  }

  // to join the room
  async join (room) {
    // wait for connection
    this.roomSet = room;
    let peerId = await _open(this.peer);
    let stream = null;
    let meshRoom = null;

    await this.setDeviceAvailability();
    try {
      stream = await navigator.mediaDevices.getUserMedia(this.constraints);
    } catch (err) {
      logger.error('[Skyway] Error on getUserMedia', { operation: 'webrtc:getUserMedia', payload: err.message || err,event_type:56,lesson_id:this.lessonId });
      throw err;
    }

    // if blur effect is active, replace stream with canvas stream
    if (this.videoEffect !== 'none') {
      stream = await this.prepareCanvasStream(stream);
    }

    // join in the room
    // in skyway's specifications if room is not exist, create it
    try {
      meshRoom = this.peer.joinRoom(room, {
        mode: 'mesh',
        stream,
        videoReceiveEnabled: !this.constraints.video
      });
      logger.debug('[Skyway] Mesh room', { payload: meshRoom && meshRoom.name });
    } catch (err) {
      logger.error('[Skyway] Error on peer joinRoom', { operation: 'webrtc:joinRoom', payload: err.message || err,event_type:57,lesson_id:this.lessonId });
      throw err;
    }

    // register the event handler to room
    try {
      for (let { event, resolve } of this.events) {
        meshRoom.on(event, resolve);
      }
    } catch (err) {
      logger.error('[Skyway] Error on adding listener', { payload: err.message || err,event_type:52,lesson_id:this.lessonId });
      throw err;
    }

    this.stream = stream;
    this.meshRoom = meshRoom;

    this.setStreamConfig();

    let rtc = this.peer

    return { peerId, stream, rtc };
  }

  filterDevice(kind, device) {
    // [Safari] newly plugged in media device id is empty and permission denied on camera won't work
    const isBrowserSafari = new UAParser().getBrowser().name.includes('Safari');
    return device.kind === kind && (isBrowserSafari || device.deviceId);
  }

  // set video & audio constraints based on the device availability
  setDeviceAvailability() {
    return new Promise((resolve, reject) => {
      navigator.mediaDevices.enumerateDevices().then(devices => {
        const cameras = devices.filter(device => {
          // [Safari] newly plugged in media device id is empty and permission denied on camera won't work
          const isBrowserSafari = new UAParser().getBrowser().name.includes('Safari');
          const cameraDeviceId = isBrowserSafari || device.deviceId;
          return device.kind === 'videoinput' && cameraDeviceId;
        });
        const mics = devices.filter(device => this.filterDevice('audioinput', device));
        const speakers = devices.filter(device => this.filterDevice('audiooutput', device));

        if (mics.length === 0) {
          reject({ name: 'NoMediaError', msg: 'No media devices found' });
          logger.error('[Skyway] Error on setDeviceAvailability', { operation: 'webrtc:setDeviceAvailability', payload: 'No media devices found',event_type:59,lesson_id:this.lessonId });
        }

        if (cameras.length === 0) {
          this.enableVideo = false;
          logger.error('[Skyway] Error on setDeviceAvailability', {
            operation: 'webrtc:setDeviceAvailability',
            payload: 'No camera found'
          });
        }

        //check if devicedId is present to the list of detected devices
        this.videoDevice = this.videoDevice && cameras.map(device => device.deviceId).includes(this.videoDevice) ? this.videoDevice : null;
        this.audioDevice = this.audioDevice && mics.map(device => device.deviceId).includes(this.audioDevice) ? this.audioDevice : null;

        this.constraints.video = cameras.length > 0 ? {
          deviceId: this.videoDevice ? { exact: this.videoDevice } : undefined,
        } : false;

        this.constraints.audio = {
          deviceId: this.audioDevice ? { exact: this.audioDevice } : undefined
        };

        // add audio output in devices list
        let payload =  { cameras: cameras, mics: mics }
        if (speakers) {
          payload.speakers = speakers;
        }

        logger.info('Detected media devices', { operation: 'devices:list', payload: payload });
        resolve({ cameras: cameras, mics: mics });
      });
    });
  }

  async setVideoDevice(deviceId) {
    const oldValue = this.videoDevice;
    this.videoDevice = deviceId;
    if (this.isScreenShare) {
      return;
    }
    const stream = await this.republishStream();
    if (stream.hasOwnProperty('error')) {
      this.videoDevice = oldValue;
    }
    return stream;
  }

  async setAudioDevice(deviceId) {
    const oldValue = this.audioDevice;
    this.audioDevice = deviceId;
    if (this.isScreenShare) {
      return;
    }
    const stream = await this.republishStream();
    if (stream.hasOwnProperty('error')) {
      this.audioDevice = oldValue;
    }
    return stream;
  }

  // share screen
  async shareScreen(mode) {
    try {
      if (mode) {
        this.isScreenShare = true;
        // get user allow and choice the display screen
        let constrainst = this.shareScreenConstraints;
        const isBrowserSafari = new UAParser().getBrowser().name.includes('Safari');
        const isBrowserVersionTarget = (parseFloat(new UAParser().getBrowser().version) > 16);
        // error on macOS ventura, possibly regarding share screen with strict contraints - https://w3c.github.io/mediacapture-screen-share/#dom-mediadevices-getdisplaymedia
        // upon investigation this latest OS with Safari (latest at the time) will be targetted - 16.x
        if (isBrowserSafari && isBrowserVersionTarget) {
          constrainst = {};
        }
        const screenStream = await navigator.mediaDevices.getDisplayMedia(constrainst);
        if (screenStream) {
          // Add voice track to shareScreen from current user peer stream
          this.stream.getAudioTracks().forEach(track => {
            screenStream.addTrack(track);
          });
          this.stream = screenStream;
        }
        this.meshRoom.replaceStream(this.stream);
        return {
          stream: this.stream,
          mode: true
        };
      }
      this.isScreenShare = false;
      return await this.republishStream();

    } catch (error) {
      logger.error('[Skyway] Error on shareScreen', { operation: 'webrtc:shareScreen', payload: error.message || error,event_type:61,lesson_id:this.lessonId });
      return { error };
    }
  }

  // Set current devices/stream states
  setStreamConfig() {
    if (this.stream.getAudioTracks().length) {
      this.stream.getAudioTracks()[0].enabled = this.enableAudio;
    }
    if (this.stream.getVideoTracks().length) {
      this.stream.getVideoTracks()[0].enabled = this.enableVideo;
    }
  }

  // reset mic config when media devices change
  async updateDeviceStream() {
    try {
      await this.setDeviceAvailability();

      //let the webrtc use the default cam of machine
      if (this.constraints.video) {
        this.constraints.video.deviceId = undefined;
      }

      //let the webrtc use the default mic of machine
      this.constraints.audio.deviceId = undefined;
      const stream = await this.republishStream();

      return stream;
    } catch (error) {
      logger.error('[Skyway] Error on updateDeviceStream', { operation: 'webrtc:updateDeviceStream', payload: error.message || error,event_type:62,lesson_id:this.lessonId });
      return { error };
    }
  }

  async setVideoEffect(effect, virtualBgModel) {
    if (!this.virtualBackground) {
      this.virtualBackground = virtualBgModel;
    }
    this.videoEffect = effect;

    try {
      this.virtualBackground.setEffect(effect);
      return await this.republishStream();
    } catch (error) {
      return { error };
    }
  }

  async prepareCanvasStream(localStream) {
    return new Promise(resolve => {
      let video = document.querySelector('.for-process-video');
      if (video.srcObject) {
        video.srcObject.getTracks().forEach(track => track.stop());
        video.srcObject = null;
      }
      video.srcObject = localStream;

      video.onloadeddata = async () => {
        await this.virtualBackground.performSegmentation(this.videoDevice);
        const canvas = document.querySelector('.virtual-bg-video');
        if (canvas) {
          canvas.getContext('2d');
          const canvasStream = canvas.captureStream();
    
          // append video stream audio track into canvas stream
          localStream.getAudioTracks().forEach(track => {
            canvasStream.addTrack(track);
          });
          resolve(canvasStream);
        } else {
          resolve(localStream);
        }
      };
    });
  }

  async republishStream() {
    try {
      this.constraints = this.videoDevice ?
        Object.assign(this.constraints, { video: Object.assign(this.videoProfile, { deviceId: this.videoDevice }) }) :
        this.constraints;
      this.constraints = this.audioDevice ?
        Object.assign(this.constraints, { audio: { deviceId: this.audioDevice } }) :
        this.constraints;

      // Check if new constraints are applied
      logger.debug(`New Device Constraints: ${JSON.stringify(this.constraints)}`);
      const localStream = await navigator.mediaDevices.getUserMedia(this.constraints);

      // if blur effect is active, replace stream with canvas stream
      let canvasStream;
      if (this.videoEffect !== 'none') {
        canvasStream = await this.prepareCanvasStream(localStream);
      }

      this.stream.getTracks().forEach(track => track.stop());
      if (localStream) {
        this.stream = this.videoEffect === 'none' ? localStream : canvasStream;
        this.setStreamConfig();
      }

      this.meshRoom.replaceStream(this.stream);

      return {
        stream: this.stream,
        mode: true
      };
    } catch (error) {
      logger.error('[Skyway] Error on republishStream', { operation: 'webrtc:republishStream', payload: error.message || error,event_type:63,lesson_id:this.lessonId });
      return { error };
    }
  }

  // enable/disable camera
  toggleCamera(state) {
    this.enableVideo = state;
    if (this.stream && this.stream.getVideoTracks().length > 0 && !this.isScreenShare) {
      this.stream.getVideoTracks()[0].enabled = state;
    }
  }

  // enable/disable microphone
  toggleMic(state) {
    this.enableAudio = state;
    if (this.stream && this.stream.getAudioTracks().length > 0) {
      this.stream.getAudioTracks()[0].enabled = state;
    }
  }

  // disconnet the current connect and leave room
  disconnect () {
    // release device
    if (this.stream) {
      for (let track of this.stream.getTracks()) {
        track.stop();
      }
    }

    // leave skyway room
    if (this.meshRoom) {
      this.meshRoom.close();
    }

    // destroy peer
    if (this.peer) {
      this.peer.destroy();
    }

    logger.info('[Skyway] Rtc leave', { operation: 'webrtc:disconnect' })
  }

  // push event from client
  on (event, resolve) {
    this.events.push({ event, resolve });
  }

  // check if remote user id already exists
  remoteUserExists(peerId) {
    return this.remoteUsers.filter(item => item.peerId == peerId).length > 0
  }

  // get rtc peer connection server
  getRTCPeerConnectionServer() {
    const peerConnection = Object.values(this.meshRoom.connections)[0];

    if (peerConnection) {
      peerConnection[0]._negotiator._pc.onicegatheringstatechange = (evt) => {
        if (evt.target.iceGatheringState === 'complete') {
          logger.info('[Skyway] RTC ICE Server: ' + this.rtcServer, { operation: 'webrtc:ice-server' });
        }
      }
    }
  }

  // set on event to skyway connection
  setOnEvent() {
    // Todo make this variables call directly to .on() method
    let enableAudio = this.enableAudio;
    let enableVideo = this.enableVideo;

    // on remote user joined the room
    this.on('stream', peerStream => {
      logger.info('[Skyway] Join: ' + peerStream.peerId, { operation: 'webrtc:remote-user-join' });
      if (this.remoteUserExists(peerStream.peerId)) return;

      this.getRTCPeerConnectionServer();

      this.remoteUsers.push({
        peerId: peerStream.peerId,
        stream: peerStream
      });
    });

    // check the server used in rtc (STUN/TURN Server)
    this.on('candidate', (evt) => {
      const iceCandidates = {
        relay: 'TURN',
        srflx: 'STUN',
        prflx: 'STUN',
        host: 'Host'
      }

      const candidate = evt.candidate.candidate
        ? evt.candidate.candidate.split("typ")[1].split(" ")[1] // Firefox: parse candidate info after `typ` string
        : evt.candidate.type

      this.rtcServer = iceCandidates[candidate] ? iceCandidates[candidate] : candidate;
    })

    // remove peer on left
    this.on('peerLeave', peerId => {
      logger.info('[Skyway] Disconnect:' + peerId, { operation: 'webrtc:remote-user-left' });
      for (let idx in this.remoteUsers) {
        if (this.remoteUsers[idx].peerId === peerId) {
          this.remoteUsers.splice(idx, 1);
        }
      }
    });

    // on connection to signalling server
    this.on('open', () => {
      this.socket.emit('signal', { type: 'rtc', connected: true });
      this.toggleMic(enableAudio);

      if (this.constraints.video) {
        this.toggleCamera(enableVideo);
      }
    });

    // on closed of connection
    this.on('close', () => {
      this.socket.emit('signal', { type: 'rtc', connected: false });
    });

    // emit lost connection to server error
    this.peer.on('error', error => {
      logger.error('[Skyway] Error on peer', { operation: 'webrtc:setOnEvent', payload: error.message || error,event_type:64,lesson_id:this.lessonId });
      if (error.type === 'socket-error') {
        this.socket.emit('signal', { type: 'rtc', connected: false });
      }
    });
  }
}

export default SkywayAdapter;
